import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { FlowDetail, FlowReport, ReportRange } from '@eombor/contracts';
import type { ApiClientError } from '../../../shared/api/client.js';
import { flowApi, type FlowNode, type FlowTarget, type FlowView } from '../api/flow.api.js';

/**
 * Statistika so'rovi.
 *
 * Kalitga daraja ham, ota tugun ham, davr ham kiradi: tuzilma bo'ylab
 * yurganda har qadam KESHDA qoladi va orqaga qaytish so'rovsiz bo'ladi.
 * Rahbar aynan shunday ishlaydi — pastga tushib, qaytib, qo'shnisiga
 * kirib solishtiradi.
 */
export const flowKeys = {
  all: ['report', 'flow'] as const,
  node: (range: ReportRange, node: FlowNode | null, view: FlowView) =>
    [
      'report',
      'flow',
      /* `auto` — serverning o'zi tanlagan daraja. Kalitda alohida qiymat,
         chunki u boshqa so'rov: javobda daraja ham o'zgarishi mumkin. */
      node?.level ?? 'auto',
      node?.parentId ?? '',
      range.from,
      range.to,
      view.q,
      view.sort,
      view.page,
    ] as const,
};

/** Kunlik agregat — bir daqiqada o'zgarmaydi, so'rov esa qimmat. */
const STALE_MS = 5 * 60_000;

/**
 * Detal paneli — alohida kalit daraxti.
 *
 * Panel jadvaldan MUSTAQIL keshlanadi: rahbar ikki satrni solishtirib
 * ko'rganda ikkinchi marta ochish so'rovsiz bo'lishi kerak, jadvalning
 * sahifasi yoki qidiruvi esa panelga tegishli emas.
 */
export const flowDetailKeys = {
  all: ['report', 'flow', 'detail'] as const,
  target: (range: ReportRange, target: FlowTarget) =>
    ['report', 'flow', 'detail', target.level, target.id, range.from, range.to] as const,
};

export const useFlowDetail = (
  range: ReportRange,
  target: FlowTarget | null,
): UseQueryResult<FlowDetail, ApiClientError> =>
  useQuery<FlowDetail, ApiClientError>({
    /* Panel yopiq bo'lsa so'rov ketmaydi (`enabled`), lekin kalit
       baribir kerak — shuning uchun yopiqdagi qiymat ahamiyatsiz. */
    queryKey:
      target === null ? flowDetailKeys.all : flowDetailKeys.target(range, target),
    queryFn: () => flowApi.detail(range, target as FlowTarget),
    enabled: target !== null,
    staleTime: STALE_MS,
  });

export const useFlowReport = (
  range: ReportRange,
  node: FlowNode | null,
  view: FlowView,
): UseQueryResult<FlowReport, ApiClientError> =>
  useQuery<FlowReport, ApiClientError>({
    queryKey: flowKeys.node(range, node, view),
    queryFn: () => flowApi.report(range, node, view),
    staleTime: STALE_MS,
    /*
     * Oldingi daraja EKRANDA qoladi.
     *
     * Satr bosilganda jadval skeletonga aylansa, ko'z yo'qotadi: odam
     * qaysi satrni bosganini va qayerga tushganini unutadi. Eski satrlar
     * bir lahza turib, keyin yangisiga almashadi.
     *
     * Qidiruvda bu ayniqsa muhim: har harfda ro'yxat skeletonga
     * aylansa, yozib bo'lgunicha ekran miltillab turadi.
     */
    placeholderData: (previous) => previous,
  });
